<template><div><BlogPost :author="$frontmatter.author" :posted="parse($frontmatter.posted, 'y-M-d', new Date())" :heading="$frontmatter.heading" :excerpt="$frontmatter.excerpt">
<h2 id="tldr" tabindex="-1" class="d-docsite--header-2">TLDR <a class="header-anchor d-link d-docsite--link d-link" href="#tldr" aria-hidden="true">#</a></h2>
<dt-notice kind="error" title="Migration Required" :show-close="false" class="d-wmx100p d-my-200 dialtone-doc-notice">
</dt-notice>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Base color stops (black, white, gray, red, green, blue, yellow, orange, purple, magenta, gold) are renamed to a standard 12-stop scale.</li>
<li class="d-docsite--list-element">Use the <a href="#migration-tool-color-stops" class="d-docsite--link d-link">Color Stops Migration Tool</a> to rename old stops.</li>
<li class="d-docsite--list-element">Use the <a href="#migration-tool-base-to-semantic" class="d-docsite--link d-link">Base to Semantic Migration Tool</a> to upgrade base color utilities and CSS tokens to semantic equivalents.</li>
<li class="d-docsite--list-element">Deprecated stops have been removed and will no longer resolve, e.g. <code v-pre>var(--dt-color-purple-350)</code>.</li>
</ul>
<h2 id="overview" tabindex="-1" class="d-docsite--header-2">Overview <a class="header-anchor d-link d-docsite--link d-link" href="#overview" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Six of the base color ramps (<code v-pre>purple</code>, <code v-pre>blue</code>, <code v-pre>magenta</code>, <code v-pre>gold</code>, <code v-pre>green</code>, <code v-pre>red</code>) previously used irregular stop numbers (<code v-pre>250</code>, <code v-pre>350</code>, <code v-pre>425</code>, etc.). This was previously necessary as part of mid-2025's Dialpad Rebrand. Some colors like <code v-pre>black</code> already used a 12-stop scale and remain unchanged.</p>
<p class="d-docsite--paragraph">All colors now follow a consistent 12-stop scale:</p>
<div class="language-text" data-highlighter="prismjs" data-ext="text"><pre v-pre><code class="language-text"><span class="line">50 | 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900 | 950 | 1000</span>
<span class="line"></span></code></pre>
</div><p class="d-docsite--paragraph"><strong>Only names changed.</strong> They've just moved to new positions in the scale. For example, the color for <code v-pre>purple-350</code> is now <code v-pre>purple-500</code>.</p>
<h2 id="examples-color-stop-renames" tabindex="-1" class="d-docsite--header-2">Examples: Color Stop Renames <a class="header-anchor d-link d-docsite--link d-link" href="#examples-color-stop-renames" aria-hidden="true">#</a></h2>
<div class="d-d-grid d-g-200 d-g-cols1 md:d-g-cols2">
<div>
<h3 id="before" tabindex="-1" class="d-docsite--header-3">Before <a class="header-anchor d-link d-docsite--link d-link" href="#before" aria-hidden="true">#</a></h3>
<div class="language-css" data-highlighter="prismjs" data-ext="css"><pre v-pre><code class="language-css"><span class="line"><span class="token selector">.foo</span> <span class="token punctuation">{</span></span>
<span class="line">  <span class="token property">color</span><span class="token punctuation">:</span> <span class="token function">var</span><span class="token punctuation">(</span>--dt-color-purple-350<span class="token punctuation">)</span><span class="token punctuation">;</span></span>
<span class="line">  <span class="token property">background</span><span class="token punctuation">:</span> <span class="token function">var</span><span class="token punctuation">(</span>--dt-color-red-450<span class="token punctuation">)</span><span class="token punctuation">;</span></span>
<span class="line"><span class="token punctuation">}</span></span>
<span class="line"></span></code></pre>
</div></div>
<div>
<h3 id="after" tabindex="-1" class="d-docsite--header-3">After <a class="header-anchor d-link d-docsite--link d-link" href="#after" aria-hidden="true">#</a></h3>
<div class="language-css" data-highlighter="prismjs" data-ext="css"><pre v-pre><code class="language-css"><span class="line"><span class="token selector">.foo</span> <span class="token punctuation">{</span></span>
<span class="line">  <span class="token property">color</span><span class="token punctuation">:</span> <span class="token function">var</span><span class="token punctuation">(</span>--dt-color-purple-500<span class="token punctuation">)</span><span class="token punctuation">;</span></span>
<span class="line">  <span class="token property">background</span><span class="token punctuation">:</span> <span class="token function">var</span><span class="token punctuation">(</span>--dt-color-red-600<span class="token punctuation">)</span><span class="token punctuation">;</span></span>
<span class="line"><span class="token punctuation">}</span></span>
<span class="line"></span></code></pre>
</div></div>
</div>
<div class="d-d-grid d-g-200 d-g-cols1 md:d-g-cols2">
<div>
<h3 id="before-utility-classes" tabindex="-1" class="d-docsite--header-3">Before (utility classes) <a class="header-anchor d-link d-docsite--link d-link" href="#before-utility-classes" aria-hidden="true">#</a></h3>
<div class="language-html" data-highlighter="prismjs" data-ext="html"><pre v-pre><code class="language-html"><span class="line"><span class="token tag"><span class="token tag"><span class="token punctuation">&lt;</span>div</span> <span class="token attr-name">class</span><span class="token attr-value"><span class="token punctuation attr-equals">=</span><span class="token punctuation">"</span>d-bgc-purple-350 d-fc-blue-425<span class="token punctuation">"</span></span><span class="token punctuation">></span></span></span>
<span class="line">  ...</span>
<span class="line"><span class="token tag"><span class="token tag"><span class="token punctuation">&lt;/</span>div</span><span class="token punctuation">></span></span></span>
<span class="line"></span></code></pre>
</div></div>
<div>
<h3 id="after-1" tabindex="-1" class="d-docsite--header-3">After <a class="header-anchor d-link d-docsite--link d-link" href="#after-1" aria-hidden="true">#</a></h3>
<div class="language-html" data-highlighter="prismjs" data-ext="html"><pre v-pre><code class="language-html"><span class="line"><span class="token tag"><span class="token tag"><span class="token punctuation">&lt;</span>div</span> <span class="token attr-name">class</span><span class="token attr-value"><span class="token punctuation attr-equals">=</span><span class="token punctuation">"</span>d-bgc-purple-500 d-fc-blue-500<span class="token punctuation">"</span></span><span class="token punctuation">></span></span></span>
<span class="line">  ...</span>
<span class="line"><span class="token tag"><span class="token tag"><span class="token punctuation">&lt;/</span>div</span><span class="token punctuation">></span></span></span>
<span class="line"></span></code></pre>
</div></div>
</div>
<h2 id="color-stop-rename-reference" tabindex="-1" class="d-docsite--header-2">Color Stop Rename Reference <a class="header-anchor d-link d-docsite--link d-link" href="#color-stop-rename-reference" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Stops not listed (e.g. <code v-pre>50</code>, <code v-pre>100</code>, <code v-pre>200</code>, <code v-pre>1000</code>) are unchanged for all colors. Blue, gold, green, and red also keep <code v-pre>300</code> unchanged. Colors not listed here already use a 12-stop scale and remain unchanged.</p>
<h3 id="purple" tabindex="-1" class="d-docsite--header-3">Purple <a class="header-anchor d-link d-docsite--link d-link" href="#purple" aria-hidden="true">#</a></h3>
<table>
<thead>
<tr>
<th>Old Stop</th>
<th>New Stop</th>
</tr>
</thead>
<tbody>
<tr>
<td><code v-pre>250</code></td>
<td><code v-pre>300</code></td>
</tr>
<tr>
<td><code v-pre>300</code></td>
<td><code v-pre>400</code></td>
</tr>
<tr>
<td><code v-pre>350</code></td>
<td><code v-pre>500</code></td>
</tr>
<tr>
<td><code v-pre>400</code></td>
<td><code v-pre>600</code></td>
</tr>
<tr>
<td><code v-pre>450</code></td>
<td><code v-pre>700</code></td>
</tr>
<tr>
<td><code v-pre>500</code></td>
<td><code v-pre>800</code></td>
</tr>
<tr>
<td><code v-pre>550</code></td>
<td><code v-pre>900</code></td>
</tr>
<tr>
<td><code v-pre>600</code></td>
<td><code v-pre>950</code></td>
</tr>
</tbody>
</table>
<h3 id="blue" tabindex="-1" class="d-docsite--header-3">Blue <a class="header-anchor d-link d-docsite--link d-link" href="#blue" aria-hidden="true">#</a></h3>
<table>
<thead>
<tr>
<th>Old Stop</th>
<th>New Stop</th>
</tr>
</thead>
<tbody>
<tr>
<td><code v-pre>425</code></td>
<td><code v-pre>500</code></td>
</tr>
<tr>
<td><code v-pre>450</code></td>
<td><code v-pre>600</code></td>
</tr>
<tr>
<td><code v-pre>475</code></td>
<td><code v-pre>700</code></td>
</tr>
<tr>
<td><code v-pre>500</code></td>
<td><code v-pre>800</code></td>
</tr>
<tr>
<td><code v-pre>600</code></td>
<td><code v-pre>900</code></td>
</tr>
<tr>
<td><code v-pre>900</code></td>
<td><code v-pre>950</code></td>
</tr>
</tbody>
</table>
<h3 id="magenta" tabindex="-1" class="d-docsite--header-3">Magenta <a class="header-anchor d-link d-docsite--link d-link" href="#magenta" aria-hidden="true">#</a></h3>
<table>
<thead>
<tr>
<th>Old Stop</th>
<th>New Stop</th>
</tr>
</thead>
<tbody>
<tr>
<td><code v-pre>250</code></td>
<td><code v-pre>300</code></td>
</tr>
<tr>
<td><code v-pre>300</code></td>
<td><code v-pre>400</code></td>
</tr>
<tr>
<td><code v-pre>400</code></td>
<td><code v-pre>500</code></td>
</tr>
<tr>
<td><code v-pre>425</code></td>
<td><code v-pre>600</code></td>
</tr>
<tr>
<td><code v-pre>475</code></td>
<td><code v-pre>700</code></td>
</tr>
<tr>
<td><code v-pre>500</code></td>
<td><code v-pre>800</code></td>
</tr>
<tr>
<td><code v-pre>600</code></td>
<td><code v-pre>900</code></td>
</tr>
<tr>
<td><code v-pre>900</code></td>
<td><code v-pre>950</code></td>
</tr>
</tbody>
</table>
<h3 id="gold" tabindex="-1" class="d-docsite--header-3">Gold <a class="header-anchor d-link d-docsite--link d-link" href="#gold" aria-hidden="true">#</a></h3>
<table>
<thead>
<tr>
<th>Old Stop</th>
<th>New Stop</th>
</tr>
</thead>
<tbody>
<tr>
<td><code v-pre>350</code></td>
<td><code v-pre>400</code></td>
</tr>
<tr>
<td><code v-pre>400</code></td>
<td><code v-pre>500</code></td>
</tr>
<tr>
<td><code v-pre>450</code></td>
<td><code v-pre>600</code></td>
</tr>
<tr>
<td><code v-pre>500</code></td>
<td><code v-pre>700</code></td>
</tr>
<tr>
<td><code v-pre>600</code></td>
<td><code v-pre>800</code></td>
</tr>
<tr>
<td><code v-pre>700</code></td>
<td><code v-pre>900</code></td>
</tr>
<tr>
<td><code v-pre>900</code></td>
<td><code v-pre>950</code></td>
</tr>
</tbody>
</table>
<h3 id="green" tabindex="-1" class="d-docsite--header-3">Green <a class="header-anchor d-link d-docsite--link d-link" href="#green" aria-hidden="true">#</a></h3>
<table>
<thead>
<tr>
<th>Old Stop</th>
<th>New Stop</th>
</tr>
</thead>
<tbody>
<tr>
<td><code v-pre>350</code></td>
<td><code v-pre>400</code></td>
</tr>
<tr>
<td><code v-pre>400</code></td>
<td><code v-pre>500</code></td>
</tr>
<tr>
<td><code v-pre>425</code></td>
<td><code v-pre>600</code></td>
</tr>
<tr>
<td><code v-pre>475</code></td>
<td><code v-pre>700</code></td>
</tr>
<tr>
<td><code v-pre>500</code></td>
<td><code v-pre>800</code></td>
</tr>
<tr>
<td><code v-pre>600</code></td>
<td><code v-pre>900</code></td>
</tr>
<tr>
<td><code v-pre>900</code></td>
<td><code v-pre>950</code></td>
</tr>
</tbody>
</table>
<h3 id="red" tabindex="-1" class="d-docsite--header-3">Red <a class="header-anchor d-link d-docsite--link d-link" href="#red" aria-hidden="true">#</a></h3>
<table>
<thead>
<tr>
<th>Old Stop</th>
<th>New Stop</th>
</tr>
</thead>
<tbody>
<tr>
<td><code v-pre>350</code></td>
<td><code v-pre>400</code></td>
</tr>
<tr>
<td><code v-pre>400</code></td>
<td><code v-pre>500</code></td>
</tr>
<tr>
<td><code v-pre>450</code></td>
<td><code v-pre>600</code></td>
</tr>
<tr>
<td><code v-pre>500</code></td>
<td><code v-pre>700</code></td>
</tr>
<tr>
<td><code v-pre>600</code></td>
<td><code v-pre>800</code></td>
</tr>
<tr>
<td><code v-pre>700</code></td>
<td><code v-pre>900</code></td>
</tr>
<tr>
<td><code v-pre>900</code></td>
<td><code v-pre>950</code></td>
</tr>
</tbody>
</table>
<h2 id="migration-tool-color-stops" tabindex="-1" class="d-docsite--header-2">Migration Tool: Color Stops <a class="header-anchor d-link d-docsite--link d-link" href="#migration-tool-color-stops" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph"><code v-pre>dialtone-migration-helper</code> scans your files for old color stop references and renames them to the new standard scale. It's included with <code v-pre>@dialpad/dialtone-css</code>.</p>
<p class="d-docsite--paragraph"><strong>The tool migrates:</strong></p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element"><code v-pre>var(--dt-color-{color}-{oldStop})</code> → <code v-pre>var(--dt-color-{color}-{newStop})</code></li>
<li class="d-docsite--list-element">Utility classes: <code v-pre>d-bgc-{color}-{oldStop}</code>, <code v-pre>d-fc-*</code>, <code v-pre>d-bc-*</code>, <code v-pre>d-bgg-from-*</code>, <code v-pre>d-bgg-to-*</code></li>
<li class="d-docsite--list-element">HSL variants: <code v-pre>-h</code>, <code v-pre>-s</code>, <code v-pre>-l</code>, <code v-pre>-hsl</code></li>
</ul>
<h3 id="usage" tabindex="-1" class="d-docsite--header-3">Usage <a class="header-anchor d-link d-docsite--link d-link" href="#usage" aria-hidden="true">#</a></h3>
<div class="language-bash" data-highlighter="prismjs" data-ext="sh"><pre v-pre><code class="language-bash"><span class="line">npx dialtone-migration-helper <span class="token parameter variable">--cwd</span> ./src</span>
<span class="line"><span class="token comment"># Select "color stops" from the config list</span></span>
<span class="line"></span></code></pre>
</div><h4 class="d-docsite--header-4">Apply All Changes</h4>
<div class="language-bash" data-highlighter="prismjs" data-ext="sh"><pre v-pre><code class="language-bash"><span class="line">npx dialtone-migration-helper <span class="token parameter variable">--cwd</span> ./src <span class="token parameter variable">--force</span></span>
<span class="line"><span class="token comment"># Select "color stops" from the config list</span></span>
<span class="line"></span></code></pre>
</div><h3 id="file-types-processed" tabindex="-1" class="d-docsite--header-3">File Types Processed <a class="header-anchor d-link d-docsite--link d-link" href="#file-types-processed" aria-hidden="true">#</a></h3>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element"><strong>Stylesheets</strong>: CSS, LESS, SCSS, SASS, Stylus</li>
<li class="d-docsite--list-element"><strong>Templates</strong>: HTML, Vue, Markdown</li>
<li class="d-docsite--list-element"><strong>Scripts</strong>: JavaScript, TypeScript, JSX, TSX</li>
</ul>
<h2 id="adopting-semantic-color-tokens" tabindex="-1" class="d-docsite--header-2">Adopting Semantic Color Tokens <a class="header-anchor d-link d-docsite--link d-link" href="#adopting-semantic-color-tokens" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">After renaming stops, many base color usages have a direct semantic equivalent. For example, <code v-pre>d-fc-red-600</code> is the same color as <code v-pre>d-fc-critical</code>. The semantic version is theme-aware, adapting automatically to dark mode, high contrast, and brand variants.</p>
<p class="d-docsite--paragraph">The base-to-semantic migration tool auto-replaces only where the mapping is unambiguous. It uses context to determine the correct semantic category:</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element"><strong>Utility class prefix</strong> tells the category: <code v-pre>d-fc-*</code> → foreground, <code v-pre>d-bgc-*</code> → surface, <code v-pre>d-bc-*</code> → border</li>
<li class="d-docsite--list-element"><strong>CSS property</strong> tells the category: <code v-pre>color:</code> → foreground, <code v-pre>background[-color]:</code> → surface, <code v-pre>border[-*][-color]:</code> → border</li>
</ul>
<h2 id="examples-base-to-semantic" tabindex="-1" class="d-docsite--header-2">Examples: Base to Semantic <a class="header-anchor d-link d-docsite--link d-link" href="#examples-base-to-semantic" aria-hidden="true">#</a></h2>
<div class="d-d-grid d-g-200 d-g-cols1 md:d-g-cols2">
<div>
<h3 id="before-utility-classes-1" tabindex="-1" class="d-docsite--header-3">Before (utility classes) <a class="header-anchor d-link d-docsite--link d-link" href="#before-utility-classes-1" aria-hidden="true">#</a></h3>
<div class="language-html" data-highlighter="prismjs" data-ext="html"><pre v-pre><code class="language-html"><span class="line"><span class="token tag"><span class="token tag"><span class="token punctuation">&lt;</span>p</span> <span class="token attr-name">class</span><span class="token attr-value"><span class="token punctuation attr-equals">=</span><span class="token punctuation">"</span>d-fc-red-600<span class="token punctuation">"</span></span><span class="token punctuation">></span></span>Error message<span class="token tag"><span class="token tag"><span class="token punctuation">&lt;/</span>p</span><span class="token punctuation">></span></span></span>
<span class="line"><span class="token tag"><span class="token tag"><span class="token punctuation">&lt;</span>div</span> <span class="token attr-name">class</span><span class="token attr-value"><span class="token punctuation attr-equals">=</span><span class="token punctuation">"</span>d-bgc-black-100<span class="token punctuation">"</span></span><span class="token punctuation">></span></span>Card<span class="token tag"><span class="token tag"><span class="token punctuation">&lt;/</span>div</span><span class="token punctuation">></span></span></span>
<span class="line"><span class="token tag"><span class="token tag"><span class="token punctuation">&lt;</span>div</span> <span class="token attr-name">class</span><span class="token attr-value"><span class="token punctuation attr-equals">=</span><span class="token punctuation">"</span>d-bc-green-700<span class="token punctuation">"</span></span><span class="token punctuation">></span></span>Success<span class="token tag"><span class="token tag"><span class="token punctuation">&lt;/</span>div</span><span class="token punctuation">></span></span></span>
<span class="line"></span></code></pre>
</div></div>
<div>
<h3 id="after-2" tabindex="-1" class="d-docsite--header-3">After <a class="header-anchor d-link d-docsite--link d-link" href="#after-2" aria-hidden="true">#</a></h3>
<div class="language-html" data-highlighter="prismjs" data-ext="html"><pre v-pre><code class="language-html"><span class="line"><span class="token tag"><span class="token tag"><span class="token punctuation">&lt;</span>p</span> <span class="token attr-name">class</span><span class="token attr-value"><span class="token punctuation attr-equals">=</span><span class="token punctuation">"</span>d-fc-critical<span class="token punctuation">"</span></span><span class="token punctuation">></span></span>Error message<span class="token tag"><span class="token tag"><span class="token punctuation">&lt;/</span>p</span><span class="token punctuation">></span></span></span>
<span class="line"><span class="token tag"><span class="token tag"><span class="token punctuation">&lt;</span>div</span> <span class="token attr-name">class</span><span class="token attr-value"><span class="token punctuation attr-equals">=</span><span class="token punctuation">"</span>d-bgc-secondary<span class="token punctuation">"</span></span><span class="token punctuation">></span></span>Card<span class="token tag"><span class="token tag"><span class="token punctuation">&lt;/</span>div</span><span class="token punctuation">></span></span></span>
<span class="line"><span class="token tag"><span class="token tag"><span class="token punctuation">&lt;</span>div</span> <span class="token attr-name">class</span><span class="token attr-value"><span class="token punctuation attr-equals">=</span><span class="token punctuation">"</span>d-bc-success<span class="token punctuation">"</span></span><span class="token punctuation">></span></span>Success<span class="token tag"><span class="token tag"><span class="token punctuation">&lt;/</span>div</span><span class="token punctuation">></span></span></span>
<span class="line"></span></code></pre>
</div></div>
</div>
<div class="d-d-grid d-g-200 d-g-cols1 md:d-g-cols2">
<div>
<h3 id="before-css-properties" tabindex="-1" class="d-docsite--header-3">Before (CSS properties) <a class="header-anchor d-link d-docsite--link d-link" href="#before-css-properties" aria-hidden="true">#</a></h3>
<div class="language-css" data-highlighter="prismjs" data-ext="css"><pre v-pre><code class="language-css"><span class="line"><span class="token selector">.alert</span> <span class="token punctuation">{</span></span>
<span class="line">  <span class="token property">color</span><span class="token punctuation">:</span> <span class="token function">var</span><span class="token punctuation">(</span>--dt-color-red-600<span class="token punctuation">)</span><span class="token punctuation">;</span></span>
<span class="line">  <span class="token property">background-color</span><span class="token punctuation">:</span> <span class="token function">var</span><span class="token punctuation">(</span>--dt-color-red-100<span class="token punctuation">)</span><span class="token punctuation">;</span></span>
<span class="line">  <span class="token property">border-color</span><span class="token punctuation">:</span> <span class="token function">var</span><span class="token punctuation">(</span>--dt-color-red-600<span class="token punctuation">)</span><span class="token punctuation">;</span></span>
<span class="line"><span class="token punctuation">}</span></span>
<span class="line"></span></code></pre>
</div></div>
<div>
<h3 id="after-3" tabindex="-1" class="d-docsite--header-3">After <a class="header-anchor d-link d-docsite--link d-link" href="#after-3" aria-hidden="true">#</a></h3>
<div class="language-css" data-highlighter="prismjs" data-ext="css"><pre v-pre><code class="language-css"><span class="line"><span class="token selector">.alert</span> <span class="token punctuation">{</span></span>
<span class="line">  <span class="token property">color</span><span class="token punctuation">:</span> <span class="token function">var</span><span class="token punctuation">(</span>--dt-color-foreground-critical<span class="token punctuation">)</span><span class="token punctuation">;</span></span>
<span class="line">  <span class="token property">background-color</span><span class="token punctuation">:</span> <span class="token function">var</span><span class="token punctuation">(</span>--dt-color-surface-critical<span class="token punctuation">)</span><span class="token punctuation">;</span></span>
<span class="line">  <span class="token property">border-color</span><span class="token punctuation">:</span> <span class="token function">var</span><span class="token punctuation">(</span>--dt-color-border-critical<span class="token punctuation">)</span><span class="token punctuation">;</span></span>
<span class="line"><span class="token punctuation">}</span></span>
<span class="line"></span></code></pre>
</div></div>
</div>
<div class="d-d-grid d-g-200 d-g-cols1 md:d-g-cols2">
<div>
<h3 id="before-border-shorthand" tabindex="-1" class="d-docsite--header-3">Before (border shorthand) <a class="header-anchor d-link d-docsite--link d-link" href="#before-border-shorthand" aria-hidden="true">#</a></h3>
<div class="language-css" data-highlighter="prismjs" data-ext="css"><pre v-pre><code class="language-css"><span class="line"><span class="token selector">.foo</span> <span class="token punctuation">{</span></span>
<span class="line">  <span class="token property">border</span><span class="token punctuation">:</span> <span class="token function">var</span><span class="token punctuation">(</span>--dt-size-border-100<span class="token punctuation">)</span> solid</span>
<span class="line">    <span class="token function">var</span><span class="token punctuation">(</span>--dt-color-red-600<span class="token punctuation">)</span><span class="token punctuation">;</span></span>
<span class="line"><span class="token punctuation">}</span></span>
<span class="line"></span></code></pre>
</div></div>
<div>
<h3 id="after-4" tabindex="-1" class="d-docsite--header-3">After <a class="header-anchor d-link d-docsite--link d-link" href="#after-4" aria-hidden="true">#</a></h3>
<div class="language-css" data-highlighter="prismjs" data-ext="css"><pre v-pre><code class="language-css"><span class="line"><span class="token selector">.foo</span> <span class="token punctuation">{</span></span>
<span class="line">  <span class="token property">border</span><span class="token punctuation">:</span> <span class="token function">var</span><span class="token punctuation">(</span>--dt-size-border-100<span class="token punctuation">)</span> solid</span>
<span class="line">    <span class="token function">var</span><span class="token punctuation">(</span>--dt-color-border-critical<span class="token punctuation">)</span><span class="token punctuation">;</span></span>
<span class="line"><span class="token punctuation">}</span></span>
<span class="line"></span></code></pre>
</div></div>
</div>
<p class="d-docsite--paragraph">Notice that the same base color <code v-pre>red-600</code> maps to <strong>different semantics</strong> depending on which CSS property it's used in: <code v-pre>foreground-critical</code> for text, <code v-pre>surface-critical-strong</code> for backgrounds, and <code v-pre>border-critical</code> for borders.</p>
<h2 id="migration-tool-base-to-semantic" tabindex="-1" class="d-docsite--header-2">Migration Tool: Base to Semantic <a class="header-anchor d-link d-docsite--link d-link" href="#migration-tool-base-to-semantic" aria-hidden="true">#</a></h2>
<div class="language-bash" data-highlighter="prismjs" data-ext="sh"><pre v-pre><code class="language-bash"><span class="line">npx dialtone-migration-helper <span class="token parameter variable">--cwd</span> ./src</span>
<span class="line"><span class="token comment"># Select "base to semantic" from the config list</span></span>
<span class="line"></span></code></pre>
</div><h3 id="what-the-tool-auto-replaces" tabindex="-1" class="d-docsite--header-3">What the tool auto-replaces <a class="header-anchor d-link d-docsite--link d-link" href="#what-the-tool-auto-replaces" aria-hidden="true">#</a></h3>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Utility classes: <code v-pre>d-fc-*</code>, <code v-pre>d-bgc-*</code>, <code v-pre>d-bc-*</code>, <code v-pre>d-divide-*</code> with mapped base colors</li>
<li class="d-docsite--list-element">CSS declarations: <code v-pre>color:</code>, <code v-pre>background[-color]:</code>, <code v-pre>border[-side][-color]:</code> with mapped base color vars</li>
<li class="d-docsite--list-element">Supports shorthand properties (<code v-pre>background:</code>, <code v-pre>border:</code>)</li>
<li class="d-docsite--list-element">Supports physical and logical border properties (<code v-pre>border-top-color</code>, <code v-pre>border-block-start</code>, <code v-pre>border-inline-end-color</code>, etc.)</li>
</ul>
<h3 id="what-requires-manual-review" tabindex="-1" class="d-docsite--header-3">What requires manual review <a class="header-anchor d-link d-docsite--link d-link" href="#what-requires-manual-review" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">The tool leaves unmapped base colors unchanged. Dialtone's ESLint rule (<code v-pre>deprecated-base-color-classes</code>) and Stylelint rule (<code v-pre>no-base-color-tokens</code>) will flag these for manual review:</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Unmapped colors (magenta, mid-range stops without a semantic equivalent)</li>
<li class="d-docsite--list-element">Custom property definitions (<code v-pre>--my-var: var(--dt-color-*)</code>)</li>
<li class="d-docsite--list-element">SVG properties (<code v-pre>fill</code>, <code v-pre>stroke</code>)</li>
<li class="d-docsite--list-element">HSL decomposed values (<code v-pre>var(--dt-color-red-600-h)</code>)</li>
</ul>
<h2 id="semantic-color-reference" tabindex="-1" class="d-docsite--header-2">Semantic Color Reference <a class="header-anchor d-link d-docsite--link d-link" href="#semantic-color-reference" aria-hidden="true">#</a></h2>
<h3 id="foreground" tabindex="-1" class="d-docsite--header-3">Foreground <a class="header-anchor d-link d-docsite--link d-link" href="#foreground" aria-hidden="true">#</a></h3>
<table>
<thead>
<tr>
<th>Base Color</th>
<th>Semantic Class</th>
<th>Semantic Token</th>
</tr>
</thead>
<tbody>
<tr>
<td><code v-pre>black-600</code></td>
<td><code v-pre>d-fc-tertiary</code></td>
<td><code v-pre>var(--dt-color-foreground-tertiary)</code></td>
</tr>
<tr>
<td><code v-pre>black-700</code></td>
<td><code v-pre>d-fc-secondary</code></td>
<td><code v-pre>var(--dt-color-foreground-secondary)</code></td>
</tr>
<tr>
<td><code v-pre>black-900</code></td>
<td><code v-pre>d-fc-primary</code></td>
<td><code v-pre>var(--dt-color-foreground-primary)</code></td>
</tr>
<tr>
<td><code v-pre>red-600</code></td>
<td><code v-pre>d-fc-critical</code></td>
<td><code v-pre>var(--dt-color-foreground-critical)</code></td>
</tr>
<tr>
<td><code v-pre>red-700</code></td>
<td><code v-pre>d-fc-critical-strong</code></td>
<td><code v-pre>var(--dt-color-foreground-critical-strong)</code></td>
</tr>
<tr>
<td><code v-pre>green-800</code></td>
<td><code v-pre>d-fc-positive</code></td>
<td><code v-pre>var(--dt-color-foreground-positive)</code></td>
</tr>
<tr>
<td><code v-pre>green-900</code></td>
<td><code v-pre>d-fc-positive-strong</code></td>
<td><code v-pre>var(--dt-color-foreground-positive-strong)</code></td>
</tr>
<tr>
<td><code v-pre>gold-800</code></td>
<td><code v-pre>d-fc-warning</code></td>
<td><code v-pre>var(--dt-color-foreground-warning)</code></td>
</tr>
</tbody>
</table>
<h3 id="surface" tabindex="-1" class="d-docsite--header-3">Surface <a class="header-anchor d-link d-docsite--link d-link" href="#surface" aria-hidden="true">#</a></h3>
<table>
<thead>
<tr>
<th>Base Color</th>
<th>Semantic Class</th>
<th>Semantic Token</th>
</tr>
</thead>
<tbody>
<tr>
<td><code v-pre>black-50</code></td>
<td><code v-pre>d-bgc-primary</code></td>
<td><code v-pre>var(--dt-color-surface-primary)</code></td>
</tr>
<tr>
<td><code v-pre>black-100</code></td>
<td><code v-pre>d-bgc-secondary</code></td>
<td><code v-pre>var(--dt-color-surface-secondary)</code></td>
</tr>
<tr>
<td><code v-pre>black-200</code></td>
<td><code v-pre>d-bgc-moderate</code></td>
<td><code v-pre>var(--dt-color-surface-moderate)</code></td>
</tr>
<tr>
<td><code v-pre>black-300</code></td>
<td><code v-pre>d-bgc-bold</code></td>
<td><code v-pre>var(--dt-color-surface-bold)</code></td>
</tr>
<tr>
<td><code v-pre>black-600</code></td>
<td><code v-pre>d-bgc-strong</code></td>
<td><code v-pre>var(--dt-color-surface-strong)</code></td>
</tr>
<tr>
<td><code v-pre>black-800</code></td>
<td><code v-pre>d-bgc-contrast</code></td>
<td><code v-pre>var(--dt-color-surface-contrast)</code></td>
</tr>
<tr>
<td><code v-pre>red-50</code></td>
<td><code v-pre>d-bgc-critical-subtle</code></td>
<td><code v-pre>var(--dt-color-surface-critical-subtle)</code></td>
</tr>
<tr>
<td><code v-pre>red-100</code></td>
<td><code v-pre>d-bgc-critical</code></td>
<td><code v-pre>var(--dt-color-surface-critical)</code></td>
</tr>
<tr>
<td><code v-pre>red-600</code></td>
<td><code v-pre>d-bgc-critical-strong</code></td>
<td><code v-pre>var(--dt-color-surface-critical-strong)</code></td>
</tr>
<tr>
<td><code v-pre>gold-50</code></td>
<td><code v-pre>d-bgc-warning-subtle</code></td>
<td><code v-pre>var(--dt-color-surface-warning-subtle)</code></td>
</tr>
<tr>
<td><code v-pre>gold-100</code></td>
<td><code v-pre>d-bgc-warning</code></td>
<td><code v-pre>var(--dt-color-surface-warning)</code></td>
</tr>
<tr>
<td><code v-pre>gold-400</code></td>
<td><code v-pre>d-bgc-warning-strong</code></td>
<td><code v-pre>var(--dt-color-surface-warning-strong)</code></td>
</tr>
<tr>
<td><code v-pre>green-50</code></td>
<td><code v-pre>d-bgc-success-subtle</code></td>
<td><code v-pre>var(--dt-color-surface-success-subtle)</code></td>
</tr>
<tr>
<td><code v-pre>green-100</code></td>
<td><code v-pre>d-bgc-success</code></td>
<td><code v-pre>var(--dt-color-surface-success)</code></td>
</tr>
<tr>
<td><code v-pre>green-800</code></td>
<td><code v-pre>d-bgc-success-strong</code></td>
<td><code v-pre>var(--dt-color-surface-success-strong)</code></td>
</tr>
<tr>
<td><code v-pre>blue-50</code></td>
<td><code v-pre>d-bgc-info-subtle</code></td>
<td><code v-pre>var(--dt-color-surface-info-subtle)</code></td>
</tr>
<tr>
<td><code v-pre>blue-100</code></td>
<td><code v-pre>d-bgc-info</code></td>
<td><code v-pre>var(--dt-color-surface-info)</code></td>
</tr>
<tr>
<td><code v-pre>blue-800</code></td>
<td><code v-pre>d-bgc-info-strong</code></td>
<td><code v-pre>var(--dt-color-surface-info-strong)</code></td>
</tr>
<tr>
<td><code v-pre>purple-50</code></td>
<td><code v-pre>d-bgc-brand-subtle</code></td>
<td><code v-pre>var(--dt-color-surface-brand-subtle)</code></td>
</tr>
<tr>
<td><code v-pre>purple-100</code></td>
<td><code v-pre>d-bgc-brand</code></td>
<td><code v-pre>var(--dt-color-surface-brand)</code></td>
</tr>
<tr>
<td><code v-pre>purple-600</code></td>
<td><code v-pre>d-bgc-brand-strong</code></td>
<td><code v-pre>var(--dt-color-surface-brand-strong)</code></td>
</tr>
</tbody>
</table>
<h3 id="border" tabindex="-1" class="d-docsite--header-3">Border <a class="header-anchor d-link d-docsite--link d-link" href="#border" aria-hidden="true">#</a></h3>
<table>
<thead>
<tr>
<th>Base Color</th>
<th>Semantic Class</th>
<th>Semantic Token</th>
</tr>
</thead>
<tbody>
<tr>
<td><code v-pre>red-300</code></td>
<td><code v-pre>d-bc-critical-subtle</code></td>
<td><code v-pre>var(--dt-color-border-critical-subtle)</code></td>
</tr>
<tr>
<td><code v-pre>red-600</code></td>
<td><code v-pre>d-bc-critical</code></td>
<td><code v-pre>var(--dt-color-border-critical)</code></td>
</tr>
<tr>
<td><code v-pre>red-800</code></td>
<td><code v-pre>d-bc-critical-strong</code></td>
<td><code v-pre>var(--dt-color-border-critical-strong)</code></td>
</tr>
<tr>
<td><code v-pre>green-300</code></td>
<td><code v-pre>d-bc-success-subtle</code></td>
<td><code v-pre>var(--dt-color-border-success-subtle)</code></td>
</tr>
<tr>
<td><code v-pre>green-700</code></td>
<td><code v-pre>d-bc-success</code></td>
<td><code v-pre>var(--dt-color-border-success)</code></td>
</tr>
<tr>
<td><code v-pre>green-900</code></td>
<td><code v-pre>d-bc-success-strong</code></td>
<td><code v-pre>var(--dt-color-border-success-strong)</code></td>
</tr>
<tr>
<td><code v-pre>gold-300</code></td>
<td><code v-pre>d-bc-warning-subtle</code></td>
<td><code v-pre>var(--dt-color-border-warning-subtle)</code></td>
</tr>
<tr>
<td><code v-pre>gold-500</code></td>
<td><code v-pre>d-bc-warning</code></td>
<td><code v-pre>var(--dt-color-border-warning)</code></td>
</tr>
<tr>
<td><code v-pre>gold-700</code></td>
<td><code v-pre>d-bc-warning-strong</code></td>
<td><code v-pre>var(--dt-color-border-warning-strong)</code></td>
</tr>
<tr>
<td><code v-pre>purple-300</code></td>
<td><code v-pre>d-bc-brand-subtle</code></td>
<td><code v-pre>var(--dt-color-border-brand-subtle)</code></td>
</tr>
<tr>
<td><code v-pre>purple-600</code></td>
<td><code v-pre>d-bc-brand</code></td>
<td><code v-pre>var(--dt-color-border-brand)</code></td>
</tr>
<tr>
<td><code v-pre>purple-800</code></td>
<td><code v-pre>d-bc-brand-strong</code></td>
<td><code v-pre>var(--dt-color-border-brand-strong)</code></td>
</tr>
<tr>
<td><code v-pre>blue-500</code></td>
<td><code v-pre>d-bc-focus</code></td>
<td><code v-pre>var(--dt-color-border-focus)</code></td>
</tr>
</tbody>
</table>
<h2 id="breaking-changes" tabindex="-1" class="d-docsite--header-2">Breaking Changes <a class="header-anchor d-link d-docsite--link d-link" href="#breaking-changes" aria-hidden="true">#</a></h2>
<dt-notice kind="warning" :show-close="false" class="d-wmx100p d-my-200 dialtone-doc-notice">
Old color stops have been <strong>removed</strong>. Consumers must run the color stops migration tool before upgrading. Base color usage is deprecated. Adopt semantic tokens for theme compatibility.
</dt-notice>
</BlogPost>
</div></template>


<script setup>
import BlogPost from '@baseComponents/BlogPost.vue';
import { parse } from 'date-fns';
</script>